import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Spinner, Alert } from 'react-bootstrap';
import { getsensormetrics, getraymondrangesensormetrics, getraymondcountsensormetrics } from '../../../service/telemetricDashboardService';
import { buildFilterObject } from '../utils/filterUtils';
import { useBSOCMetrics } from '../hooks/useMetrics';

const SensorMetricsCards = ({ selectedManufacturer = 'toyota', chartFilters }) => {
	const [sensorData, setSensorData] = useState(null);
	const [raymondRangeData, setRaymondRangeData] = useState(null);
	const [raymondCountData, setRaymondCountData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Fetch BSOC metrics using custom hook
	const { bsocData } = useBSOCMetrics(selectedManufacturer, chartFilters);

	// Check if it's a Raymond manufacturer
	const isRaymond = selectedManufacturer.startsWith('raymond');

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);

				if (isRaymond) {
					// Fetch Raymond data
					const filters = {
						siteids: chartFilters.siteids || [],
						vehiclemodels: chartFilters.vehiclemodels || [],
						vehicletypes: chartFilters.vehicletypes || []
					};

					const [rangeResponse, countResponse] = await Promise.all([
						getraymondrangesensormetrics(selectedManufacturer, filters),
						getraymondcountsensormetrics(selectedManufacturer, filters)
					]);

					setRaymondRangeData(rangeResponse.data);
					setRaymondCountData(countResponse.data);
				} else {
					// Fetch Toyota data
					const filters = buildFilterObject(chartFilters);
					const response = await getsensormetrics(filters);
					setSensorData(response.data);
				}
			} catch (err) {
				console.error('Error fetching sensor metrics:', err);
				setError('Failed to load sensor metrics');
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [selectedManufacturer, chartFilters.siteids, chartFilters.vehiclemodels, chartFilters.vehicletypes]); // Watch selectedManufacturer to re-fetch when switching between raymond-us and raymond-eu

	// Helper function to get vehicle count from API data
	const getVehicleCount = (category, subcategory) => {
		if (!sensorData || !Array.isArray(sensorData)) return null;

		const record = sensorData.find(item =>
			item.TelematicsErrorCategoryEnglish === category &&
			item.TelematicsErrorSubCategoryEnglish === subcategory
		);

		return record ? record.VehicleCount : null;
	};

	// Helper function to get Raymond range metrics (min/max/avg)
	const getRaymondRangeMetric = (sbid) => {
		if (!raymondRangeData || !Array.isArray(raymondRangeData)) {
			return { min: '-', max: '-', avg: '-' };
		}

		const record = raymondRangeData.find(item => item.SBID === sbid);
		if (!record) {
			return { min: '-', max: '-', avg: '-' };
		}

		return {
			min: record.MinValue !== null && record.MinValue !== undefined ? Math.round(record.MinValue) : '-',
			max: record.MaxValue !== null && record.MaxValue !== undefined ? Math.round(record.MaxValue) : '-',
			avg: record.AvgValue !== null && record.AvgValue !== undefined ? Math.round(record.AvgValue) : '-'
		};
	};

	// Helper function to get Raymond count metrics
	const getRaymondCountMetric = (sbid) => {
		if (!raymondCountData || !Array.isArray(raymondCountData)) return '-';

		const record = raymondCountData.find(item => item.SBID === sbid);
		return record && record.EventCount !== null ? record.EventCount : '-';
	};

	// Helper function to get metric object with dynamic styling
	const getMetric = (label, category, subcategory, isStaticValue = false, staticValue = null) => {
		if (isStaticValue) {
			return {
				label,
				value: staticValue || '-',
				colorClass: staticValue && staticValue !== '-' ? 'text-primary' : 'text-muted'
			};
		}

		const count = getVehicleCount(category, subcategory);
		const hasData = count !== null && count > 0;

		return {
			label,
			value: hasData ? count : '-',
			colorClass: hasData ? 'text-primary' : 'text-muted'
		};
	};

	// JSON configuration for Toyota cards
	const toyotaCardsData = [
		{
			id: 'dhu',
			title: 'DHU',
			badge: '24 hrs',
			description: null,
			metrics: [
				getMetric('FOTA', 'DHU', 'FOTA'),
				getMetric('Reboot', 'DHU', 'Reboot'),
				getMetric('Sleep', 'DHU', 'Sleep'),
				getMetric('Storage', 'DHU', 'Storage'),
				getMetric('Temperature', 'DHU', 'Temperature'),
				getMetric('General', 'DHU', 'General'),
				getMetric('Security', 'DHU', 'Security')
			]
		},
		{
			id: 'cloud-communication',
			title: 'Cloud Communication',
			badge: '24 hrs',
			description: null,
			metrics: [
				getMetric('Service Levels', 'Cloud Communication', 'Service Levels'),
				getMetric('Cloud Sync', 'Cloud Communication', 'Cloud Sync')
			]
		},
		{
			id: 'vehicle',
			title: 'Vehicle',
			badge: '24 hrs',
			description: null,
			metrics: [
				getMetric('Controller Communication', 'Vehicle', 'Controller Communication'),
				getMetric('Wired Communication', 'Vehicle', 'Wired Communication')
			]
		},
		{
			id: 'wireless',
			title: 'Wireless',
			badge: '24 hrs',
			description: null,
			metrics: [
				getMetric('Wifi', 'Wireless', 'Wifi'),
				getMetric('Cellular', 'Wireless', 'Cellular'),
				getMetric('Bluetooth', 'Wireless', 'Bluetooth'),
				getMetric('GNSS', 'Wireless', 'GNSS')
			]
		},
		{
			id: 'hardware-peripherals',
			title: 'Hardware Peripherals',
			badge: '24 hrs',
			description: null,
			metrics: [
				getMetric('Impact Sensor', 'Hardware Peripherals', 'Impact Sensor'),
				getMetric('Badge Reader', 'Hardware Peripherals', 'Badge Reader'),
				getMetric('Telematics Display', 'Hardware Peripherals', 'Telematics Display')
			]
		},
		{
			id: 'bsoc',
			title: 'BSOC',
			badge: '24 hrs',
			metrics: [
				getMetric('Minimum', null, null, true, bsocData.min ? `${bsocData.min} ${bsocData.unit || '%'}` : '-'),
				getMetric('Maximum', null, null, true, bsocData.max ? `${bsocData.max} ${bsocData.unit || '%'}` : '-'),
				getMetric('Average', null, null, true, bsocData.avg ? `${bsocData.avg} ${bsocData.unit || '%'}` : '-')
			]
		}
	];

	// JSON configuration for Raymond cards based on the attached image
	const raymondCardsData = [
		{
			id: 'battery',
			title: 'Battery',
			badge: '24 hrs',
			description: null,
			metrics: [
				{
					label: 'Battery Voltage',
					value: getRaymondRangeMetric(4193),
					colorClass: 'text-primary',
					type: 'minMaxAvg'
				},
				{
					label: 'Battery Water Level',
					value: getRaymondRangeMetric(4353),
					colorClass: 'text-primary',
					type: 'minMaxAvg'
				},
				{
					label: 'Battery Temp',
					value: getRaymondRangeMetric(4196),
					colorClass: 'text-primary',
					type: 'minMaxAvg'
				}
			]
		},
		{
			id: 'eps',
			title: 'EPS',
			badge: '24 hrs',
			description: null,
			metrics: [
				{
					label: 'EPS SOC',
					value: getRaymondRangeMetric(4255),
					colorClass: 'text-primary',
					type: 'minMaxAvg'
				},
				{
					label: 'EPS Temperature',
					value: getRaymondRangeMetric(4226),
					colorClass: 'text-primary',
					type: 'minMaxAvg'
				}
			]
		},
		{
			id: 'safety-check',
			title: 'Safety Check',
			badge: '24 hrs',
			description: null,
			metrics: [
				{ label: 'Enhanced Checklist Failed', value: getRaymondCountMetric(12305), colorClass: 'text-primary' },
				{ label: 'Safety Check Bypassed', value: getRaymondCountMetric(12292), colorClass: 'text-primary' },
				{ label: 'Safety Check Failed', value: getRaymondCountMetric(12289), colorClass: 'text-primary' },
				{ label: 'Safety Check Timeout', value: getRaymondCountMetric(12291), colorClass: 'text-primary' }
			]
		},
		{
			id: 'rtls',
			title: 'RTLS',
			badge: '24 hrs',
			description: null,
			metrics: [
				{ label: 'RTLS Loss Of Tracking', value: getRaymondCountMetric(12323), colorClass: 'text-primary' },
				{ label: 'RTLS Tracking Restored', value: getRaymondCountMetric(12324), colorClass: 'text-primary' }
			]
		}
	];

	// Select appropriate cards data based on company
	const cardsData = isRaymond ? raymondCardsData : toyotaCardsData;

	const renderCard = (cardData) => (
		<Col key={cardData.id} className="mb-3" style={{ flex: '1 1 0', minWidth: isRaymond ? '300px' : '220px' }}>
			<Card className="h-100">
				<Card.Header>
					<div className="d-flex justify-content-between align-items-center">
						<span>{cardData.title}</span>
						<span className="badge bg-secondary">{cardData.badge}</span>
					</div>
				</Card.Header>
				<Card.Body className="p-0">
					{cardData.description && <p className="small text-muted mb-2 px-3 pt-2">{cardData.description}</p>}
					<Table className="mb-0" size="sm" striped hover>
						<tbody>
							{cardData.metrics.map((metric, index) => (
								<tr key={`${cardData.id}-${index}`}>
									<td className="text-muted fw-medium border-0 py-2 px-3" style={{ fontSize: '0.85rem' }}>
										{metric.label}
									</td>
									<td className={`${metric.colorClass} fw-semibold text-end border-0 py-2 px-3`} style={{ fontSize: '0.85rem' }}>
										{metric.type === 'minMaxAvg' ? (
											<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
												<div>
													<div className="text-muted" style={{ fontSize: '0.75rem' }}>Min</div>
													<div className="text-primary fw-bold">{metric.value.min}</div>
												</div>
												<div>
													<div className="text-muted" style={{ fontSize: '0.75rem' }}>Max</div>
													<div className="text-primary fw-bold">{metric.value.max}</div>
												</div>
												<div>
													<div className="text-muted" style={{ fontSize: '0.75rem' }}>Avg</div>
													<div className="text-primary fw-bold">{metric.value.avg}</div>
												</div>
											</div>
										) : (
											metric.value
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
		</Col>
	);

	// Show loading state
	if (loading) {
		return (
			<Row className="mb-4">
				<Col className="text-center py-5">
					<Spinner animation="border" role="status">
						<span className="visually-hidden">Loading...</span>
					</Spinner>
				</Col>
			</Row>
		);
	}

	// Show error state
	if (error) {
		return (
			<Row className="mb-4">
				<Col>
					<Alert variant="danger">{error}</Alert>
				</Col>
			</Row>
		);
	}

	return (
		<Row className="mb-4">
			{cardsData.map(renderCard)}
		</Row>
	);
};

export default SensorMetricsCards;

